<template>
    <div class="service-reminders-container container-fluid">
        <SearchComponent />

        <div class="service-reminders-inner container-fluid">
            <div class="row">
                <div class="col-md-9">
                    <div
                        ref="map"
                        id="service-reminders-map"
                    />
                </div>
                <div class="col-md-3">
                    <p
                        v-if="!loading && reminders.length < 1"
                        class="reminder-row-none"
                    >
                        No reminders found
                    </p>
                    <div
                        v-for="(reminder, index) in reminders"
                        :key="index"
                        :id="'reminder-' + reminder.id"
                        class="reminder-row"
                        :class="{'reminder-row-focused': focusedIndex === index }"
                    >
                        <div class="reminder-row-customer">
                            <span class="reminder-row-label">{{ index + 1 }}</span>
                            <router-link :to="{name: 'office.customers.show', params: {id: reminder.site.customer.id}}">
                                {{ reminder.site.customer.company_name }}
                            </router-link>
                        </div>

                        <div class="reminder-row-site">
                            <router-link
                                :to="{name: 'office.sites.edit', params: {customer_id: reminder.site.customer.id, id: reminder.site.id}}"
                            >
                                {{ reminder.site.name }} - {{ reminder.site.address ? reminder.site.address.postcode : 'Unknown' }}
                            </router-link>
                        </div>
                        <div class="reminder-row-month">
                            <span>{{ reminder.nice_type }} - {{ reminder.month_name }}</span>
                        </div>
                        <div class="reminder-row-actions">
                            <StatusComponent
                                :reminder="reminder"
                                class="reminder-row-status"
                            />
                            <button
                                class="btn btn-info btn-sm reminder-row-send"
                                v-if="reminder.status === 'due'"
                                :disabled="sending"
                                @click.prevent="sendReminder(reminder)"
                            >
                                <i
                                    v-show="!sending"
                                    class="far fa-envelope"
                                />
                                <i
                                    v-show="sending"
                                    class="fa fa-sync-alt fa-spin"
                                />
                                Send reminder
                            </button>
                        </div>
                        <div
                            v-if="reminder.sent"
                            class="reminder-row-sent"
                        >
                            <i
                                class="fa fa-check text-success"
                                data-toggle="tooltip"
                                data-placement="top"
                            />
                            <span>Sent at {{ formatDate(reminder.sent_at) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SearchComponent from "./SearchComponent.vue";
import {mapActions, mapState} from "vuex";
import StatusComponent from "./StatusComponent.vue";
import {initialMapOptions, centerLatLng} from '../../../include/Map';

export default {
    components: {
        StatusComponent,
        SearchComponent,
    },
    data: function () {
        return {
            map: undefined,
            focusedIndex: undefined,
        };
    },
    computed: {
        ...mapState('serviceReminders', [
            'reminders',
            'loading',
            'sending',
        ]),
        initialCenter() {
            if (!this.reminders[0]) {
                return centerLatLng;
            }
            const address = this.reminders[0].site.address;
            if (!address || !address.lat || !address.lng) {
                return undefined;
            }

            return {
                lat: address.lat,
                lng: address.lng
            };
        },
        reminderIds() {
            return this.reminders.map(r => r.id).join(',');
        },
    },
    watch: {
        reminderIds: {
            handler(newIds, oldIds) {
                if (newIds !== oldIds || !oldIds) {
                    this.initialiseMap();
                }

            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions("serviceReminders", ["sendReminder"]),
        initialiseMap() {
            window.setTimeout(() => {
                this.map = new window.google.maps.Map(this.$refs.map, initialMapOptions);

                const bounds = new window.google.maps.LatLngBounds();

                const markers = this.reminders.map((reminder, index) => {
                    const address = reminder.site.address;
                    if (!address || !address.lat || !address.lng) {
                        return undefined;
                    }

                    const coord = {
                        lat: address.lat,
                        lng: address.lng
                    };

                    bounds.extend(coord);

                    const label = `${index + 1}`;

                    const marker = new window.google.maps.Marker({
                        position: coord,
                        title: `${reminder.site.customer.company_name} ${reminder.site.name}`,
                        map: this.map,
                        label,
                    });

                    marker.addListener("click", () => {
                        const rowEl = document.getElementById(`reminder-${reminder.id}`);
                        rowEl.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                        this.focusedIndex = index;
                    });

                    return marker;
                }).filter(marker => !!marker);

                if (markers.length > 0) {
                    this.map.fitBounds(bounds);
                }
            }, 500);
        },
        formatDate(date) {
            return date
                ? window.moment(date).format("DD/MM/YYYY HH:mm")
                : "Unknown";
        },
    },
};
</script>

<style lang="scss" scoped>
#service-reminders-map {
    width: 100%;
    height: 100%;
}

.service-reminders-inner {
    .row {
        height: 80vh;

        & > div {
            overflow-y: auto;
            height: 100%;
        }
    }
}

.reminder-row {
    margin-bottom: 1rem;
    background-color: white;
    padding: 1rem;
    border-radius: 5px;
    border: 1px solid #eaeaea;

    &-focused {
        outline: 2px solid #4285F4;
    }

    &-none {
        background-color: #fff;
        text-align: center;
        height: 100%;
        padding: 2rem;
    }

    &-label {
        padding: .2em;
        font-weight: 500;
        min-width: 2.2rem;
        border-radius: 100%;
        background-color: #EA4336;
        margin-right: .25rem;
        vertical-align: middle;
        display: inline-block;
        text-align: center;
    }

    &-customer {
        font-weight: bolder;
        font-size: 1.2rem;
        margin-bottom: 1rem;
    }

    &-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 2rem;
        gap: .5rem;
    }

    &-status {
        flex-grow: 0;
        width: auto;
    }

    &-sent {
        margin-top: 1rem;
        display: flex;
        align-items: center;
        gap: .5rem;
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/office/service-reminders/MapComponent.vue